import { formatDateInTimeZone } from '../../utils/dateTime';

/**
 * Карточка «Приём звонков»: из ответа `GET /integrations/telephony-health` — что показать по каждой
 * телефонии. Вся логика здесь, а не в JSX: статус, причины отказов и подсказки проверяются тестом.
 *
 * «Сейчас» — момент, когда сервер собрал ответ (`generatedAtUtc`), а не часы браузера: «5 мин назад»
 * не врёт при сбитых часах у пользователя, а модуль остаётся чистой функцией.
 */

/** Карточка обновляется раз в минуту: админ настраивает кабинет и ждёт, появится ли звонок. */
export const INTAKE_HEALTH_POLL_MS = 60 * 1000;

/** Сервис приёма пишет учёт раз в 15 секунд; пять минут тишины — он не работает. */
export const REPORTER_STALE_MS = 5 * 60 * 1000;

/** Опрос по таймеру — только в видимой вкладке: фоновая вкладка админа гоняла бы запрос к сводкам впустую. */
export const shouldPoll = (visibilityState) => visibilityState !== 'hidden';

const PROVIDER_NAMES = {
  mango: 'Mango Office',
  megafon: 'МегаФон',
  primatel: 'Primatel',
  uis: 'UIS',
  asterisk: 'Asterisk'
};

/** Опрос API вместо вебхуков: у них нет отказов по ключу, зато есть состояние опроса. */
const POLL_PROVIDERS = new Set(['primatel', 'uis']);

/** Причины, при которых настоящий звонок телефонии отвергнут. */
const AUTH_REJECTIONS = new Set(['invalid_signature', 'unknown_key', 'invalid_token', 'not_configured']);

export const REJECTION_TEXTS = {
  invalid_signature: {
    label: 'Неверная подпись',
    hint: 'Ключ АТС совпал, а подпись — нет: на сервере не та соль (API Salt). Сверьте ключ и соль в кабинете с настройками сервера.',
    tone: 'error'
  },
  unknown_key: {
    label: 'Неверный ключ АТС',
    hint: 'Пришли запросы с ключом, которого нет на сервере: ключи в кабинете телефонии сменили — или это чужие запросы.',
    tone: 'error'
  },
  invalid_token: {
    label: 'Неверный токен',
    hint: 'Токен в кабинете телефонии не совпадает с токеном на сервере.',
    tone: 'error'
  },
  not_configured: {
    label: 'Ключ не настроен на сервере',
    hint: 'Телефония присылает звонки, а на сервере её ключ не задан. Сообщите в поддержку.',
    tone: 'error'
  },
  invalid_payload: {
    label: 'Нечитаемые данные',
    hint: 'Запрос прошёл проверку ключа, но в нём нет обязательных полей. Сообщите в поддержку.',
    tone: 'warning'
  },
  missing_credentials: {
    label: 'Запросы без ключа',
    hint: 'Обычно это сканеры из интернета: на приём звонков они не влияют.',
    tone: 'muted'
  }
};

const STATUS = {
  notConfigured: { tone: 'error', label: 'Не настроена на сервере' },
  rejecting: { tone: 'error', label: 'Отклоняет звонки' },
  pollFailing: { tone: 'error', label: 'Опрос не проходит' },
  warning: { tone: 'warning', label: 'Есть замечания' },
  idle: { tone: 'idle', label: 'Звонков за сутки не было' },
  ok: { tone: 'ok', label: 'Работает' }
};

const DATE_TIME_FORMAT = { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' };

const toMs = (value) => {
  const ms = value ? Date.parse(value) : Number.NaN;
  return Number.isNaN(ms) ? null : ms;
};

/** «только что», «12 мин назад», «3 ч назад», «2 дн. назад» — без склонений, чтобы не путаться в формах. */
export const formatAgo = (value, nowMs) => {
  const ms = toMs(value);
  if (ms === null || !Number.isFinite(nowMs)) {
    return '';
  }

  const minutes = Math.floor(Math.max(0, nowMs - ms) / 60000);
  if (minutes < 1) {
    return 'только что';
  }
  if (minutes < 60) {
    return `${minutes} мин назад`;
  }

  const hours = Math.floor(minutes / 60);
  return hours < 24 ? `${hours} ч назад` : `${Math.floor(hours / 24)} дн. назад`;
};

/** «03.11, 14:05 (12 мин назад)» в часовом поясе стенда. */
export const formatMoment = (value, { nowMs, timeZone }) => {
  if (toMs(value) === null) {
    return null;
  }

  const ago = formatAgo(value, nowMs);
  const moment = formatDateInTimeZone(value, timeZone, DATE_TIME_FORMAT);
  return ago ? `${moment} (${ago})` : moment;
};

export const telephonyName = (item) => {
  const name = PROVIDER_NAMES[item?.provider] ?? item?.provider ?? 'Телефония';
  return item?.account ? `${name} · ${item.title || item.account}` : name;
};

const describeRejection = (rejection, context) => {
  const texts = REJECTION_TEXTS[rejection.reason] ?? {
    label: rejection.reason,
    hint: '',
    tone: 'warning'
  };

  return {
    reason: rejection.reason,
    count: rejection.count,
    lastAt: formatMoment(rejection.lastAtUtc, context),
    ...texts
  };
};

/**
 * Отказы по ключу «сейчас» — если после последнего отказа не принято ни одного итога. Иначе ключ уже
 * поправили (или это был разовый чужой запрос), и звонки идут.
 */
const isRejectingNow = (item) => {
  const lastAccepted = toMs(item.lastSummaryAtUtc);
  return (item.rejections ?? []).some((rejection) => {
    if (!AUTH_REJECTIONS.has(rejection.reason)) {
      return false;
    }

    const lastRejected = toMs(rejection.lastAtUtc);
    return lastAccepted === null || (lastRejected !== null && lastRejected > lastAccepted);
  });
};

const describeProblems = (item, context) => {
  const problems = [];

  if (item.poll && item.poll.consecutiveFailures > 0 && item.poll.lastError) {
    problems.push({
      id: 'poll-error',
      tone: 'error',
      text: `Опрос не проходит: ${item.poll.lastError}`,
      hint: item.poll.lastSuccessAtUtc
        ? `Последний успешный опрос — ${formatMoment(item.poll.lastSuccessAtUtc, context)}.`
        : 'Успешных опросов ещё не было.'
    });
  }

  if (item.answeredWithoutRecording > 0) {
    problems.push({
      id: 'unrecorded',
      tone: 'warning',
      text: `Отвеченных звонков без записи за сутки: ${item.answeredWithoutRecording}`,
      hint: 'Итог звонка пришёл, а записи разговора нет. Проверьте, что в кабинете телефонии включена запись разговоров.'
    });
  }

  if (item.downloadFailures > 0) {
    problems.push({
      id: 'download-failed',
      tone: 'warning',
      text: `Не скачались записи за сутки: ${item.downloadFailures}`,
      hint: item.lastDownloadFailureAtUtc
        ? `Последняя — ${formatMoment(item.lastDownloadFailureAtUtc, context)}. Эти звонки не попадут в аналитику; сообщите в поддержку.`
        : 'Эти звонки не попадут в аналитику; сообщите в поддержку.'
    });
  }

  return problems;
};

const resolveStatus = (item, rejections, problems) => {
  if (item.configured === false) {
    return STATUS.notConfigured;
  }
  if (isRejectingNow(item)) {
    return STATUS.rejecting;
  }
  if (problems.some((problem) => problem.id === 'poll-error')) {
    return STATUS.pollFailing;
  }
  if (problems.length > 0 || rejections.some((rejection) => rejection.tone !== 'muted')) {
    return STATUS.warning;
  }
  return item.summaries > 0 ? STATUS.ok : STATUS.idle;
};

const describeFacts = (item, context) => {
  const never = 'нет с начала учёта';
  const facts = [
    {
      id: 'last-summary',
      label: 'Последний итог звонка',
      value: formatMoment(item.lastSummaryAtUtc, context) ?? never
    },
    {
      id: 'last-recording',
      label: 'Последняя запись',
      value: formatMoment(item.lastRecordingAtUtc, context) ?? never
    },
    {
      id: 'window',
      label: 'За сутки',
      value: `итогов: ${item.summaries ?? 0}, записей: ${item.recordings ?? 0}`
    }
  ];

  if (POLL_PROVIDERS.has(item.provider) && item.poll) {
    facts.push({
      id: 'last-poll',
      label: 'Последний успешный опрос',
      value: formatMoment(item.poll.lastSuccessAtUtc, context) ?? 'ещё не было'
    });
  }

  return facts;
};

export const describeTelephony = (item, context) => {
  const rejections = (item.rejections ?? []).map((rejection) => describeRejection(rejection, context));
  const problems = describeProblems(item, context);

  return {
    key: `${item.provider}:${item.account ?? ''}`,
    name: telephonyName(item),
    status: resolveStatus(item, rejections, problems),
    facts: describeFacts(item, context),
    rejections,
    problems
  };
};

/**
 * Предупреждение о самом сервисе приёма: ни разу не отчитался (не обновлён) или замолчал (не запущен).
 * `null` — всё в порядке.
 */
export const describeReporter = (response, context) => {
  const seenMs = toMs(response?.reporterSeenAtUtc);
  if (seenMs === null) {
    return (response?.telephonies ?? []).length > 0
      ? null
      : {
        tone: 'warning',
        text: 'Сервис приёма звонков ещё не прислал данных: учёт начнётся после его обновления и перезапуска.'
      };
  }

  if (context.nowMs - seenMs > REPORTER_STALE_MS) {
    return {
      tone: 'error',
      text: `Сервис приёма звонков не выходил на связь с ${formatMoment(response.reporterSeenAtUtc, context)}. Звонки сейчас не принимаются — сообщите в поддержку.`
    };
  }

  return null;
};

/** Всё, что рисует карточка. */
export const buildIntakeHealthView = (response, { timeZone } = {}) => {
  const context = { nowMs: toMs(response?.generatedAtUtc) ?? Number.NaN, timeZone };
  const telephonies = (response?.telephonies ?? []).map((item) => describeTelephony(item, context));

  return {
    reporter: describeReporter(response, context),
    telephonies,
    isEmpty: telephonies.length === 0
  };
};
